import { type FC, type MouseEvent } from 'react';

import clsx from 'clsx';

import { type MenuItem as MenuItemType } from '@/app/[locale]/(authorized)/components/Menu';
import { CornerBorders } from '@/components/common/CornerBorders';
import { Link } from '@/components/common/Link';
import { Text } from '@/components/common/Text';
import { usePathname } from '@/i18n/routing';
import { type WithClassName } from '@/types/react';

import styles from './MenuItem.module.scss';

type MenuItemProps = WithClassName & {
  item: MenuItemType;
};

export const MenuItem: FC<MenuItemProps> = (props) => {
  const {
    className,
    item: { link, sectionLink, isExact, isActive: isActiveCheck, onboardingTarget, icon, content, onClick },
  } = props;

  const pathname = usePathname();

  const isActive = isActiveCheck
    ? isActiveCheck(pathname)
    : isExact
      ? pathname === link
      : pathname.startsWith(sectionLink ?? link);

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(event);

    if (event.defaultPrevented || !onboardingTarget) return;

    window.dispatchEvent(
      new CustomEvent('onboarding:navigation-target', {
        detail: {
          onboardingTarget,
          route: link,
        },
      })
    );
  };

  return (
    <Link
      className={clsx(styles.root, { [styles.active]: isActive }, className)}
      href={link}
      onClick={handleClick}
      data-onboarding-target={onboardingTarget}
    >
      <CornerBorders className={styles.box}>
        <span className={styles.content}>
          <span className={styles.icon}>{icon}</span>
          <Text type="regular14" ellipsis>
            {content}
          </Text>
        </span>
      </CornerBorders>
    </Link>
  );
};
